import { IconButton, SingleSelectDropdown, Text, toast } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  addMemberWorkspace,
  getTrainingWorkspaceSyncStatus,
  isWorkspaceAlreadyClaimedError,
  isWorkspaceClaimNotFoundError,
  isWorkspaceNotInScopeError,
  listMembers,
  remapTrainingWorkspace,
  removeMemberWorkspace,
  type Member,
  type TrainingWorkspaceCandidate,
  type WorkspaceDrift,
  type WorkspaceSyncStatusView,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { ResourceBrowserLayout } from "@/components/browse";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";
import { HatTextModal } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { RemapConfirmModal } from "./RemapConfirmModal";
import { buildMemberOptions, driftDiffLabel, remapResultMessage } from "./workspaceSyncFormat";
import styles from "./WorkspaceSyncPanel.module.css";

type SyncData = {
  status: WorkspaceSyncStatusView;
  members: readonly Member[];
};

// 어드민 "동기화 확인" 탭. 선점 행과 training live 의 차이 4버킷(missing/name/group/미선점)을
// 표시하고 해소한다. 선점 스냅샷은 선점 시점 값으로 고정된다. 그래서 training 쪽 변경은 이
// 탭에서만 반영된다. 배포 직후에는 모든 workspace 가 unclaimed 로 표시된다. 그래서 재선점이 첫
// 운영 작업이다. 응답은 단일 응답(4 배열)이다. 그래서 테이블 소스 없이 상태 1개로 보관하고,
// 액션이 성공하면 전체를 다시 받는다.
export function WorkspaceSyncPanel(): ReactNode {
  const [syncState, setSyncState] = useState<LoadState<SyncData>>({ status: "idle" });
  // missing 행의 [선점 해제] 확인 대상(null=닫힘).
  const [releaseTarget, setReleaseTarget] = useState<WorkspaceDrift | null>(null);
  const [releaseBusy, setReleaseBusy] = useState(false);
  // drift 행의 [동기화](스냅샷 갱신) 진행 중 workspace id.
  const [syncingId, setSyncingId] = useState<string | null>(null);
  // drift 행의 [소유자 이전…] 다이얼로그 대상(null=닫힘).
  const [transferTarget, setTransferTarget] = useState<WorkspaceDrift | null>(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  // unclaimed 행별 멤버 선택과 [선점 부여] 진행 중 workspace id.
  const [claimSelection, setClaimSelection] = useState<Record<string, string>>({});
  const [claimingId, setClaimingId] = useState<string | null>(null);

  // 언마운트 후 도착한 응답이 상태를 바꾸지 않게 하는 가드(StrictMode 재마운트 시 복구).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setSyncState({ status: "loading" });
    try {
      // 멤버 목록은 이전/선점 피커의 공용 소스다. 그래서 진단과 함께 1회 병렬로 받는다.
      const [status, members] = await Promise.all([
        getTrainingWorkspaceSyncStatus(),
        listMembers(),
      ]);
      if (aliveRef.current) {
        setSyncState({ status: "success", data: { status, members } });
      }
    } catch (error) {
      if (aliveRef.current) {
        setSyncState({ status: "error", message: resolveLoadErrorMessage(error) });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const data = syncState.status === "success" ? syncState.data : null;
  const memberOptions = useMemo(() => buildMemberOptions(data?.members ?? []), [data?.members]);
  const isLoading = syncState.status === "loading";
  const totalCount =
    data === null
      ? 0
      : data.status.missing.length +
        data.status.name_drift.length +
        data.status.group_drift.length +
        data.status.unclaimed.length;

  // 선점 해제(missing). training 에서 사라진 workspace 의 선점 행을 지운다.
  const handleRelease = (): void => {
    if (releaseTarget === null || releaseBusy) {
      return;
    }
    setReleaseBusy(true);
    void removeMemberWorkspace(releaseTarget.owner.uuid, releaseTarget.training_workspace_id)
      .then(() => {
        if (!aliveRef.current) {
          return;
        }
        toast.success("선점을 해제했습니다.");
        setReleaseBusy(false);
        setReleaseTarget(null);
        void load();
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) {
          return;
        }
        toast.error(resolveLoadErrorMessage(error));
        setReleaseBusy(false);
        setReleaseTarget(null);
        void load();
      });
  };

  // 스냅샷 갱신(name/group drift). 소유자는 그대로 두고 snapshot 만 live 값으로 맞춘다.
  const handleSyncSnapshot = (drift: WorkspaceDrift): void => {
    if (syncingId !== null) {
      return;
    }
    setSyncingId(drift.training_workspace_id);
    void remapTrainingWorkspace(drift.training_workspace_id)
      .then((result) => {
        if (!aliveRef.current) {
          return;
        }
        toast.success(remapResultMessage(result.changed_fields));
        setSyncingId(null);
        void load();
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) {
          return;
        }
        toast.error(resolveRemapErrorMessage(error));
        setSyncingId(null);
        void load();
      });
  };

  // 소유자 이전. RemapConfirmModal 에서 확인하면 member_uuid 를 포함해 remap 을 보낸다.
  const handleTransfer = (memberUuid: string): void => {
    if (transferTarget === null || transferBusy) {
      return;
    }
    setTransferBusy(true);
    setTransferError(null);
    void remapTrainingWorkspace(transferTarget.training_workspace_id, { member_uuid: memberUuid })
      .then((result) => {
        if (!aliveRef.current) {
          return;
        }
        toast.success(remapResultMessage(result.changed_fields));
        setTransferBusy(false);
        setTransferTarget(null);
        void load();
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) {
          return;
        }
        setTransferBusy(false);
        setTransferError(resolveRemapErrorMessage(error));
      });
  };

  // 선점 부여(unclaimed). 고른 멤버에게 workspace 선점을 부여한다.
  const handleClaim = (workspace: TrainingWorkspaceCandidate): void => {
    const memberUuid = claimSelection[workspace.id];
    if (memberUuid === undefined || claimingId !== null) {
      return;
    }
    setClaimingId(workspace.id);
    void addMemberWorkspace(memberUuid, {
      training_workspace_id: workspace.id,
      workspace_name: workspace.name,
    })
      .then(() => {
        if (!aliveRef.current) {
          return;
        }
        toast.success(`"${workspace.name}" Workspace를 선점 부여했습니다.`);
        setClaimingId(null);
        void load();
      })
      .catch((error: unknown) => {
        if (!aliveRef.current) {
          return;
        }
        toast.error(
          isWorkspaceAlreadyClaimedError(error)
            ? "이미 다른 멤버가 선점한 Workspace입니다."
            : resolveLoadErrorMessage(error),
        );
        setClaimingId(null);
        void load();
      });
  };

  return (
    <div className={styles.panel}>
      <ResourceBrowserLayout
        ariaLabel="Workspace 동기화 확인"
        summaryCount={totalCount}
        summaryLabel="동기화 대상"
        actions={
          <IconButton
            text={isLoading ? "로딩 중…" : "새로고침"}
            size="sm"
            variant="surface"
            disabled={isLoading}
            onClick={() => void load()}
          />
        }
        error={syncState.status === "error" ? syncState.message : null}
        onRetry={() => void load()}
      >
        {isLoading || syncState.status === "idle" ? (
          <LoadingState size="lg">동기화 상태를 불러오는 중입니다.</LoadingState>
        ) : data === null ? null : (
          <div className={styles.sections}>
            <BucketSection
              title="training에서 사라진 Workspace입니다."
              caption="training에 더 이상 없는 Workspace입니다. 선점을 해제해 정리해 주세요."
              count={data.status.missing.length}
              emptyText="사라진 Workspace가 없습니다."
            >
              {data.status.missing.map((drift) => (
                <li key={drift.training_workspace_id} className={styles.row}>
                  <DriftSummary drift={drift} diff={driftDiffLabel(drift, "name")} />
                  <div className={styles.rowActions}>
                    <IconButton
                      text="선점 해제"
                      size="sm"
                      variant="surface"
                      tone="danger"
                      disabled={releaseTarget !== null}
                      onClick={() => setReleaseTarget(drift)}
                    />
                  </div>
                </li>
              ))}
            </BucketSection>

            <BucketSection
              title="이름이 변경된 Workspace입니다."
              caption="training에서 이름이 바뀌었습니다. 동기화로 스냅샷을 갱신해 주세요."
              count={data.status.name_drift.length}
              emptyText="이름이 어긋난 Workspace가 없습니다."
            >
              {data.status.name_drift.map((drift) => (
                <DriftRow
                  key={drift.training_workspace_id}
                  drift={drift}
                  diff={driftDiffLabel(drift, "name")}
                  syncing={syncingId === drift.training_workspace_id}
                  actionsDisabled={syncingId !== null}
                  onSync={() => handleSyncSnapshot(drift)}
                  onTransfer={() => setTransferTarget(drift)}
                />
              ))}
            </BucketSection>

            <BucketSection
              title="그룹이 이동된 Workspace입니다."
              caption="training에서 다른 그룹으로 옮겨졌습니다. 동기화로 스냅샷을 갱신해 주세요."
              count={data.status.group_drift.length}
              emptyText="그룹이 어긋난 Workspace가 없습니다."
            >
              {data.status.group_drift.map((drift) => (
                <DriftRow
                  key={drift.training_workspace_id}
                  drift={drift}
                  diff={driftDiffLabel(drift, "group")}
                  syncing={syncingId === drift.training_workspace_id}
                  actionsDisabled={syncingId !== null}
                  onSync={() => handleSyncSnapshot(drift)}
                  onTransfer={() => setTransferTarget(drift)}
                />
              ))}
            </BucketSection>

            <BucketSection
              title="아직 선점되지 않은 Workspace입니다."
              caption="아무도 선점하지 않아 목록에 보이지 않는 Workspace입니다. 멤버를 골라 선점을 부여해 주세요."
              count={data.status.unclaimed.length}
              emptyText="미선점 Workspace가 없습니다."
            >
              {data.status.unclaimed.map((workspace) => (
                <li key={workspace.id} className={styles.row}>
                  <div className={styles.rowInfo}>
                    <Text variant={hatcheryTextModes["body-bold"]}>{workspace.name}</Text>
                  </div>
                  <div className={styles.rowActions}>
                    <SingleSelectDropdown
                      mode={hatcheryDropdownModes.default}
                      label="멤버"
                      value={claimSelection[workspace.id] ?? null}
                      options={memberOptions}
                      placeholder="멤버를 선택해 주세요."
                      disabled={claimingId !== null}
                      onValueChange={(next) =>
                        setClaimSelection((prev) => ({ ...prev, [workspace.id]: next }))
                      }
                    />
                    <IconButton
                      text={claimingId === workspace.id ? "부여 중…" : "선점 부여"}
                      size="sm"
                      variant="solid"
                      disabled={claimingId !== null || claimSelection[workspace.id] === undefined}
                      onClick={() => handleClaim(workspace)}
                    />
                  </div>
                </li>
              ))}
            </BucketSection>
          </div>
        )}
      </ResourceBrowserLayout>

      {releaseTarget !== null ? (
        <HatTextModal
          open
          label="Workspace 선점을 해제합니다."
          caption={`"${releaseTarget.display_name}"은 training에서 사라진 Workspace입니다. ${releaseTarget.owner.display_name} 님의 선점을 해제하면 이 Workspace의 Session이 개인·팀 목록에서 사라집니다.`}
          okText="선점 해제"
          cancelText="취소"
          error
          busy={releaseBusy}
          onOkPress={handleRelease}
          onCancelPress={() => {
            if (!releaseBusy) {
              setReleaseTarget(null);
            }
          }}
        />
      ) : null}
      {transferTarget !== null ? (
        <RemapConfirmModal
          drift={transferTarget}
          memberOptions={memberOptions}
          busy={transferBusy}
          errorMessage={transferError}
          onClose={() => {
            if (!transferBusy) {
              setTransferTarget(null);
              setTransferError(null);
            }
          }}
          onConfirm={handleTransfer}
        />
      ) : null}
    </div>
  );
}

type BucketSectionProps = {
  title: string;
  caption: string;
  count: number;
  emptyText: string;
  children: ReactNode;
};

function BucketSection({ title, caption, count, emptyText, children }: BucketSectionProps) {
  return (
    <section className={styles.bucket} aria-label={title}>
      <header className={styles.bucketHeader}>
        <Text variant={hatcheryTextModes["body-bold"]}>{`${title} (${count})`}</Text>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.caption}>
          {caption}
        </Text>
      </header>
      {count === 0 ? (
        <EmptyState size="sm">{emptyText}</EmptyState>
      ) : (
        <ul className={styles.rows}>{children}</ul>
      )}
    </section>
  );
}

type DriftSummaryProps = {
  drift: WorkspaceDrift;
  diff: string;
};

function DriftSummary({ drift, diff }: DriftSummaryProps) {
  return (
    <div className={styles.rowInfo}>
      <Text variant={hatcheryTextModes["body-bold"]}>{drift.display_name}</Text>
      <span className={styles.meta}>{`소유자 ${drift.owner.display_name} · ${diff}`}</span>
    </div>
  );
}

type DriftRowProps = {
  drift: WorkspaceDrift;
  diff: string;
  syncing: boolean;
  actionsDisabled: boolean;
  onSync: () => void;
  onTransfer: () => void;
};

function DriftRow({ drift, diff, syncing, actionsDisabled, onSync, onTransfer }: DriftRowProps) {
  return (
    <li className={styles.row}>
      <DriftSummary drift={drift} diff={diff} />
      <div className={styles.rowActions}>
        <IconButton
          text={syncing ? "동기화 중…" : "동기화"}
          size="sm"
          variant="surface"
          disabled={actionsDisabled}
          onClick={onSync}
        />
        <IconButton
          text="소유자 이전…"
          size="sm"
          variant="surface"
          disabled={actionsDisabled}
          onClick={onTransfer}
        />
      </div>
    </li>
  );
}

// remap 전용 에러 문구. 422(사라짐)는 선점 해제를 안내하고, 404(미선점)는 재조회를 안내한다.
function resolveRemapErrorMessage(error: unknown): string {
  if (isWorkspaceNotInScopeError(error)) {
    return "training에서 사라진 Workspace라 갱신할 수 없습니다. 선점을 해제해 정리해 주세요.";
  }
  if (isWorkspaceClaimNotFoundError(error)) {
    return "선점된 Workspace가 아닙니다. 목록을 새로고침합니다.";
  }
  return resolveLoadErrorMessage(error);
}
